// Reading a stored course plan: the shape normalization and the "which lesson
// is the learner on" rule that LessonsView renders and locks against. Pure, so
// both are testable without mounting the view.

import { valuesOf } from "@/scripts/rtdbLists.js";

/**
 * The plan as nested plain arrays, whichever shape RTDB returned each
 * collection in. Normalizing once here means the view and its computeds can
 * iterate freely instead of each guarding the object and null-hole forms.
 * @param {unknown} courseData a single course node (courses/{uid}/{lang}/{id})
 * @returns {object[]} units, each with a `weeks` array of `lessons` arrays
 */
export function normalizePlanUnits(courseData) {
  return valuesOf(courseData?.plan?.units).map((unit) => ({
    ...unit,
    weeks: valuesOf(unit.weeks).map((week) => ({
      ...week,
      lessons: valuesOf(week.lessons),
    })),
  }));
}

/**
 * The lesson the learner is currently on: the first in plan order that is
 * unfinished, or the last completed one when the whole plan is done. Every
 * lesson after it is locked, so this is what decides how far the course is
 * open - and why the Voice Ledger CTA has to resolve to the same lesson, or
 * it would only ever raise the locked toast.
 *
 * Completion is checked BEFORE the untouched test on purpose. A lesson holding
 * a completedAt but no startedAt used to read as untouched, which pinned the
 * current lesson there and locked everything behind it permanently.
 *
 * @param {object[]} units output of normalizePlanUnits
 * @returns {{lesson: object, unit: object, week: object}|null}
 */
export function findCurrentLesson(units) {
  let lastCompleted = null;

  for (const unit of units) {
    for (const week of unit.weeks) {
      for (const lesson of week.lessons) {
        if (lesson.completedAt) {
          lastCompleted = { lesson, unit, week };
          continue;
        }
        // Started but unfinished, or never started: either way this is where
        // the learner stands, and nothing past it is open yet.
        return { lesson, unit, week };
      }
    }
  }

  return lastCompleted;
}

/**
 * Where findCurrentLesson landed, as the plan coordinates LessonsView locks
 * against.
 * @param {object[]} units output of normalizePlanUnits
 * @returns {{unitNumber: number, weekNumber: number, lessonNumber: number}|null}
 */
export function findLatestLesson(units) {
  const current = findCurrentLesson(units);
  if (!current) return null;
  return {
    unitNumber: current.unit.number,
    weekNumber: current.week.number,
    lessonNumber: current.lesson.number,
  };
}
